import { useState } from "react";
import SectionHeading from "components/text/SectionHeading";
import { IMailchimpCampaignMetrics } from "interfaces/ICampaigns.interface";
import { Mail } from "lucide-react";
import {
  LineChart,
  Line,
  CartesianGrid,
  XAxis,
  YAxis,
  Tooltip,
  ResponsiveContainer,
} from "recharts";

const dateTimeFormatter = (value: string) =>
  new Date(value).toLocaleString("en-GB", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false, // 24h format
  });

export default function MailchimpCampaignCard({
  campaigns = [],
}: {
  campaigns: IMailchimpCampaignMetrics[];
}) {
  const [selectedCampaign, setSelectedCampaign] =
    useState<IMailchimpCampaignMetrics | null>(null);

  return (
    campaigns && (
      <div className="rounded-lg border bg-card p-4 shadow-sm xl:col-span-2 my-4">
        <SectionHeading
          title="Mailchimp"
          subtitle="Recent Mailchimp campaign performance"
        />
        <div className="mb-3 flex items-center justify-between">
          <div className="flex items-center gap-2 text-sm font-medium">
            <Mail className="h-4 w-4" /> Mailchimp campaigns
          </div>
          <button className="text-xs text-primary">View all</button>
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full table-auto text-sm">
            <thead className="text-left text-muted-foreground">
              <tr className="border-b">
                <th className="py-2 pr-4">Subject</th>
                <th className="py-2 pr-4">Opens</th>
                <th className="py-2 pr-4">Click Rate</th>
                <th className="py-2 pr-4 text-right">Orders</th>
                <th className="py-2 pr-0 text-right">Revenue</th>
              </tr>
            </thead>
            <tbody>
              {campaigns.map((c, index) => {
                let clickRateComparison: string | null = null;

                if (
                  c.industry_stats?.click_rate != null &&
                  c.click_rate != null
                ) {
                  const diff =
                    (c.click_rate - c.industry_stats.click_rate) * 100;
                  const sign = diff > 0 ? "+" : "";
                  clickRateComparison = `${sign}${diff.toFixed(
                    1
                  )}% vs industry`;
                }

                return (
                  <tr
                    key={index}
                    className="border-b last:border-0 cursor-pointer hover:bg-muted/30"
                    onClick={() => setSelectedCampaign(c)}
                  >
                    <td className="py-2 pr-4">{c.campaignName}</td>
                    <td className="py-2 pr-4">{c.opens}</td>
                    <td className="py-2 pr-4">
                      {c.click_rate != null
                        ? `${(c.click_rate * 100).toFixed(2)}%`
                        : "–"}{" "}
                      {clickRateComparison && (
                        <span className="text-muted-foreground text-sm">
                          ({clickRateComparison})
                        </span>
                      )}
                    </td>
                    <td className="py-2 pr-4 text-right">{c.orders ?? 0}</td>
                    <td className="py-2 pr-0 text-right">
                      £{c.revenue?.toLocaleString() ?? "0"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Time series chart for selected campaign */}
        {selectedCampaign && selectedCampaign.timeseries && (
          <div className="mt-6">
            <h4 className="text-sm font-medium mb-2">
              Unique Opens Over Time – {selectedCampaign.campaignName}
            </h4>
            <ResponsiveContainer width="100%" height={300}>
              <LineChart data={selectedCampaign.timeseries}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="timestamp"
                  tickFormatter={dateTimeFormatter}
                  interval="preserveStartEnd" // keeps first and last, skips middle if crowded
                  minTickGap={40} // adds breathing room, adjust as needed
                />
                <YAxis />
                <Tooltip labelFormatter={dateTimeFormatter} /> <YAxis />
                <Line
                  type="monotone"
                  dataKey="unique_opens"
                  stroke="#2563eb" // blue-600
                  strokeWidth={2}
                  dot={false}
                />
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>
    )
  );
}
